import { axiosInstance } from '.'
import {
  LOGGED_IN_USER,
  USER_CREATE_V1,
  PROMOTE_SIGNUP_V1,
  PROMOTE_SIGNUP_DIRECT_V1,
  PROMOTE_SIGNUP_MAGIC_V1,
  SET_OWN_PASSWORD_V1,
  PROMOTE_SIGNUP_EMAIL_V1,
  EMAIL_LINK_LOGIN_V1,
  PHONE_OTP_LOGIN_REQUEST_V1,
  PHONE_OTP_LOGIN_VERIFY_V1,
  PROMOTE_SIGNUP_TOKEN_VALID,
  PROMOTE_SIGNUP_COMPLETE_V1,
  SEND_VERIFICATION_LINK_V1,
  RESET_PASSWORD_V1,
  USER_SIGN_IN_V1,
  VERIFY_EMAIL_V1,
  USER_LOGOUT_V1,
  GET_PROFILE_V1,
  LOGGED_IN_USER_V1,
  UPDATE_PROFILE_V1,
  RESET_PASSWORD_TOKEN_VALID,
  GOOGLE_SIGN_IN_V1,
  GENERATE_PHONE_OTP,
  VERIFY_PHONE_OTP,
  ADMIN_INVESTOR_SIGN_IN_V1,
} from './endpoints'
import { resolveRiskProfilerRequired } from '../lib/utils/onboarding'
import {
  Admin,
  NewUser,
  Role,
  User,
  UserService,
  UtmAttributionPayload,
  Verification,
} from '../lib/types'
import { removeEmptyValues } from '../lib/utils/helpers'
import {
  buildRiskProfilePatchBody,
  RiskProfileDeclaration,
  RiskProfilerAnswers,
} from '../lib/utils/risk-profiler'

export const userServices: UserService = {
  getIsResetPasswordLinkValid: async (userId: string, code: string) => {
    try {
      return await axiosInstance.get(RESET_PASSWORD_TOKEN_VALID(code, userId))
    } catch (error: any) {
      console.log(error.response)
      return error.response
    }
  },

  // v1
  userSignUp_v1: async (userData: NewUser, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(USER_CREATE_V1, userData, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  promoteSignup_v1: async (
    payload: {
      name: string
      email: string
      mobile: string
      consent: boolean
      website?: string
      variant?: string
      page?: string
      utmAttribution?: UtmAttributionPayload
    },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(PROMOTE_SIGNUP_V1, payload, {
        headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  promoteDirectSignup_v1: async (
    payload: {
      name: string
      email: string
      password: string
      consent: boolean
      website?: string
      page?: string
      utmAttribution?: UtmAttributionPayload
    },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(PROMOTE_SIGNUP_DIRECT_V1, payload, {
        headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  promoteMagicSignup_v1: async (
    payload: {
      name: string
      email: string
      consent: boolean
      website?: string
      page?: string
      utmAttribution?: UtmAttributionPayload
    },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(PROMOTE_SIGNUP_MAGIC_V1, payload, {
        headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  /** /promote email-link signup: no password, no session; the person opens the emailed link. */
  promoteEmailSignup_v1: async (
    payload: {
      name: string
      email: string
      consent: boolean
      website?: string
      page?: string
      utmAttribution?: UtmAttributionPayload
    },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(PROMOTE_SIGNUP_EMAIL_V1, payload, { headers })
    } catch (error: any) {
      return error.response
    }
  },

  /** Emails a sign-in (or, for an unconfirmed account, confirm) link. Always 200 for a valid email. */
  requestEmailLink_v1: async (email: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(EMAIL_LINK_LOGIN_V1, { email }, { headers })
    } catch (error: any) {
      return error.response
    }
  },

  /** Sends a sign-in code to a phone that is saved and OTP-verified. Always 200. */
  requestPhoneLoginOtp_v1: async (phone: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(PHONE_OTP_LOGIN_REQUEST_V1, { phone }, { headers })
    } catch (error: any) {
      return error.response
    }
  },

  verifyPhoneLoginOtp_v1: async (phone: string, otp: string) => {
    try {
      return await axiosInstance.post(PHONE_OTP_LOGIN_VERIFY_V1, { phone, otp })
    } catch (error: any) {
      return error.response
    }
  },

  setOwnPassword_v1: async (
    payload: { currentPassword: string; password: string },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(SET_OWN_PASSWORD_V1, payload, {
        headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getIsPromoteSignupLinkValid: async (userId: string, code: string) => {
    try {
      return await axiosInstance.get(PROMOTE_SIGNUP_TOKEN_VALID(code, userId))
    } catch (error: any) {
      return error.response
    }
  },

  completePromoteSignup_v1: async (
    payload: { userId: number | string; code: string; password: string },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(PROMOTE_SIGNUP_COMPLETE_V1, payload, {
        headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  verifyEmail_v1: async (code: string, user: string, type: Verification) => {
    try {
      return await axiosInstance.get(VERIFY_EMAIL_V1(code, user, type))
    } catch (error: any) {
      return error.response
    }
  },

  sendVerificationLink_v1: async (email: string, type: Verification) => {
    try {
      return await axiosInstance.post(SEND_VERIFICATION_LINK_V1, {
        email,
        type,
      })
    } catch (error: any) {
      return error.response
    }
  },

  resetPassword_v1: async (resetPasswordData: object, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(RESET_PASSWORD_V1, resetPasswordData, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  userLogin_v1: async (
    loginData: User & { role: Role.investor },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(USER_SIGN_IN_V1, loginData, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  userLogout_v1: async (headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(USER_LOGOUT_V1, {}, { headers: headers })
    } catch (error: any) {
      return error.response
    }
  },

  adminLoginAsInvestor_v1: async (
    loginData: Admin & { role: Exclude<Role, 'investor'> },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(ADMIN_INVESTOR_SIGN_IN_V1, loginData, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getProfileForCurrentUser_v1: async (headers: { [key: string]: string }) => {
    try {
      const response = await axiosInstance.get(LOGGED_IN_USER_V1, {
        headers: headers,
      })
      return {
        data: response.data,
        status: response.status,
      }
    } catch (error: any) {
      return error
    }
  },

  getUserProfile_v1: async (headers: { [key: string]: string }) => {
    try {
      const data = await axiosInstance.get(GET_PROFILE_V1, {
        headers: headers,
      })
      const profileData = {
        countryOfResidence: data.data.country ?? '',
        email: data.data.email ?? '',
        name: data.data.name ?? '',
        firstName: data.data.first_name ?? '',
        lastName: data.data.last_name ?? '',
        dateOfBirth: data.data.date_of_birth ?? data.data.dob ?? '',
        taxResidency: data.data.tax_residency ?? '',
        citizenship: data.data.citizenship ?? '',
        phoneNumber: data.data.phone ?? '',
        communicationPhone: data.data.communication_phone ?? '',
        occupation: data.data.occupation ?? '',
        annualIncomeRange: data.data.income_range ?? data.data.annual_income_range ?? '',
        basicDetailsCompleted: data.data.basic_details_completed ?? false,
        riskProfileCompleted: data.data.risk_profile_completed ?? false,
        riskProfilerRequired: resolveRiskProfilerRequired({
          risk_profiler_required: data.data.risk_profiler_required,
          risk_profile_completed: data.data.risk_profile_completed,
        }),
        riskProfileAnswers: data.data.risk_profile_answers ?? null,
        whatsappConsent: data.data.whatsapp_consent ?? false,
        phonePreference: data.data.phone_preference ?? '',
        phoneSkipDate: data.data.phone_skip_date ?? '',
        investorReference: data.data.investor_reference ?? '',
      }
      return { data: profileData, status: data.status }
    } catch (error: any) {
      return error.response
    }
  },

  userProfileUpdate_v1: async (profileData: any, headers: { [key: string]: string }) => {
    try {
      if ('email' in profileData) delete profileData['email']
      const sanitizedData = removeEmptyValues(profileData)
      const payload: Record<string, unknown> = {
        phone: sanitizedData.phoneNumber,
        country: sanitizedData.countryOfResidence,
        address: sanitizedData.address,
      }

      if (sanitizedData.name) {
        payload.name = sanitizedData.name
      }
      if (sanitizedData.firstName) {
        payload.first_name = sanitizedData.firstName
      }
      if (sanitizedData.lastName) {
        payload.last_name = sanitizedData.lastName
      }
      if (sanitizedData.dateOfBirth) {
        payload.date_of_birth = sanitizedData.dateOfBirth
      }
      if (typeof profileData.age === 'number') {
        payload.age = profileData.age
      }
      if (sanitizedData.taxResidency) {
        payload.tax_residency = sanitizedData.taxResidency
      }
      if (sanitizedData.citizenship) {
        payload.citizenship = sanitizedData.citizenship
      }
      if (sanitizedData.communicationPhone) {
        payload.communication_phone = sanitizedData.communicationPhone
      }
      if (sanitizedData.occupation) {
        payload.occupation = sanitizedData.occupation
      }
      if (sanitizedData.annualIncomeRange) {
        payload.income_range = sanitizedData.annualIncomeRange
      }
      if (profileData.basicDetailsCompleted) {
        payload.basic_details_completed = true
      }
      if ('whatsappConsent' in profileData) {
        payload.whatsapp_consent = profileData.whatsappConsent
      }
      if (profileData.phonePreference) {
        payload.phone_preference = profileData.phonePreference
      }
      const data = await axiosInstance.patch(UPDATE_PROFILE_V1, payload, {
        headers: headers,
      })
      return data
    } catch (error: any) {
      return error.response
    }
  },

  saveRiskProfile_v1: async (
    answers: RiskProfilerAnswers,
    headers: { [key: string]: string },
    declarations: RiskProfileDeclaration[] = [],
  ) => {
    try {
      const payload = buildRiskProfilePatchBody(answers, declarations)
      return await axiosInstance.patch(UPDATE_PROFILE_V1, payload, { headers })
    } catch (error: any) {
      return error.response
    }
  },

  googleSignIn_v1: async (loginData: { [key: string]: string | boolean | object | undefined }) => {
    try {
      return await axiosInstance.post(GOOGLE_SIGN_IN_V1, loginData)
    } catch (error: any) {
      return error.response
    }
  },

  generatePhoneOTP: async (phone: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(
        GENERATE_PHONE_OTP,
        { phone },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },

  verifyPhoneOTP: async (otp: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(
        VERIFY_PHONE_OTP,
        { otp },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },
}
